import {useCallback, useRef, useState} from 'react';
import {Navigate, useNavigate, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {LivenessAction, IV_DOCUMENT_IMAGE_TYPES, IV_PHOTO_SOURCE} from '@chekin/core';
import {PATHS} from 'routes/constants';
import {useSummary} from 'context/summary';
import {useDocumentSelfieDetection, useIVComputedDetails, usePath} from 'hooks';
import {Camera} from 'lucide-react';
import {Button, cn} from '@chekinapp/ui';
import {
  blurSelfiePhoto,
  CaptureDock,
  CaptureProcessFrame,
  LivenessDetection,
  PORTRAIT_FACE_FRAME,
  useIVStore,
} from '@chekin/shared';
import {SelfiePrepGuide} from '@chekin/shared/guest-form';
import {isUnattendedTerminal} from 'hooks/terminalMode';
import {useDiscardLastPhotoBack} from 'hooks/iv/useDiscardLastPhotoBack';
import {PageLayout} from 'components/PageLayout';
import {SkipForNow} from 'components/common/SkipForNow';
import {RegistrationStepSubtitle} from 'components/RegistrationProgress';
import {useHousing} from 'hooks/useHousing';
import {
  getIVCaptureChecks,
  useIVCaptureScreenText,
  useLivenessStageText,
} from '../../ivCaptureCopy';

function SelfieLivenessView() {
  const {id} = useParams();
  const {t} = useTranslation();
  const {t: tIV} = useTranslation('identity-verification');
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const {onBack: handleLeave, modal: discardModal} = useDiscardLastPhotoBack();
  const {reservationId, countryCode} = useSummary();
  const {housing} = useHousing();
  const frontPhoto = useIVStore(state => state.imagesDetails.front);
  const setLivenessResultData = useIVStore(state => state.setLivenessResultData);
  const docType = IV_DOCUMENT_IMAGE_TYPES.selfie;
  const {required} = useIVComputedDetails();
  const livenessVideoRef = useRef<Blob | null>(null);
  const [capturedSelfie, setCapturedSelfie] = useState<string | null>(null);

  const {
    isProcessing,
    processState,
    passedChecks,
    errorMessage,
    errorDescription,
    errorReason,
    isServiceFailure,
    failureCount,
    onTryAgain,
    onVerifyLater,
    canVerifyLater,
    isFinalStep,
    biomatch,
    sendSelfieToDetectFace,
  } = useDocumentSelfieDetection({docType, livenessVideo: livenessVideoRef.current});

  const {copy, title, intro, isChecking} = useIVCaptureScreenText(
    'liveness',
    processState,
    isProcessing,
    {
      presentation: 'ring',
      isFinalStep,
      biomatch,
      errorReason,
      failureCount,
    },
  );
  const isGuided = !isUnattendedTerminal();
  const [isPrepVisible, setIsPrepVisible] = useState(isGuided);
  const prepText = useIVCaptureScreenText('selfie', undefined, false, {
    presentation: 'ring',
    isIntro: true,
  });
  const livenessStageText = useLivenessStageText();
  const heading = isProcessing
    ? {title, intro}
    : isPrepVisible
    ? {title: prepText.title, intro: prepText.intro}
    : livenessStageText;

  const showsPrep = isPrepVisible && !isProcessing;

  const handleBack = () => {
    if (isGuided && !isPrepVisible && !isProcessing) {
      setIsPrepVisible(true);
      return;
    }
    handleLeave();
  };

  const handleChangePhoto = useCallback(
    async ({
      screenshot,
      performedBy,
      completedActions,
      recordingVideo,
    }: {
      screenshot: string | null;
      recordingVideo: Blob | null;
      performedBy: 'CLIENT' | 'SERVER';
      completedActions: LivenessAction[];
    }) => {
      if (!screenshot) return;

      setCapturedSelfie(screenshot);
      livenessVideoRef.current = recordingVideo;

      sendSelfieToDetectFace({
        source: IV_PHOTO_SOURCE.appCamera,
        countryCode,
        reservationId,
        image: screenshot,
      });
      setLivenessResultData({
        performedBy,
        passed: true,
        completedActions,
      });

      try {
        const previewPhoto = await blurSelfiePhoto({photo: screenshot});
        if (previewPhoto) {
          setCapturedSelfie(previewPhoto);
        }
      } catch (error) {
        throw new Error(error as string);
      }
    },
    [countryCode, reservationId, sendSelfieToDetectFace, setLivenessResultData],
  );

  if (!frontPhoto.uploaded) {
    return <Navigate to={`../../${PATHS.verification.main}/${id ?? ''}`} />;
  }

  return (
    <PageLayout
      className={cn('iv-selfie-liveness-view', showsPrep && 'lg:flex-1')}
      title={heading.title}
      intro={heading.intro}
      align={isChecking ? 'center' : undefined}
      backLabel={t('back')}
      onBack={handleBack}
      titleAction={isChecking ? undefined : <SkipForNow />}
      mobileTitle={t('guest_registration_title')}
      mobileSubtitle={<RegistrationStepSubtitle />}
    >
      <div className={cn('flex flex-col gap-8', showsPrep && 'flex-1')}>
        {isProcessing ? (
          <CaptureProcessFrame
            className="ph-no-capture"
            variant="face"
            checking={{
              checks: getIVCaptureChecks('liveness'),
              passedChecks,
              isFinalStep,
            }}
            state={processState}
            image={capturedSelfie}
            scanningLabel={copy.scanningLabel}
            successLabel={copy.successLabel}
            errorTitle={errorMessage || copy.errorTitle}
            errorDescription={errorDescription || copy.errorDescription}
            hasKnownCause={Boolean(errorReason)}
            isServiceFailure={isServiceFailure}
            onRetry={onTryAgain}
            onVerifyLater={canVerifyLater ? onVerifyLater : undefined}
          />
        ) : isPrepVisible ? (
          <>
            <SelfiePrepGuide />
            <CaptureDock>
              <Button
                onClick={() => setIsPrepVisible(false)}
                className="w-full max-w-[340px] lg:w-[280px]"
              >
                <Camera />
                {tIV('open_camera')}
              </Button>
            </CaptureDock>
          </>
        ) : (
          <LivenessDetection
            className={cn('ph-no-capture', PORTRAIT_FACE_FRAME)}
            faceGuideSize="max"
            forceLivenessMechanism={housing?.force_liveness_mechanism}
            onComplete={handleChangePhoto}
            isRequired={required}
            onVerifyLater={() => {
              navigate(getFullPath(PATHS.addPersonalDataForm));
            }}
          />
        )}
      </div>
      {discardModal}
    </PageLayout>
  );
}

export {SelfieLivenessView};
